import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, filter, map, of, switchMap, take } from 'rxjs';

import { AppRoutes } from '@gfe/app.paths';

import { POST_LOGIN_REDIRECT_KEY } from '@core/constants/auth.constants';
import { DataspaceRoutingService } from '@core/services/dataspace-routing/dataspace-routing.service';
import { selectAuthState, selectUser } from '@core/store/auth/auth.selectors';

export const dataspaceRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);
  const store = inject(Store);
  const dataspaceRoutingService = inject(DataspaceRoutingService);

  return combineLatest([store.select(selectUser), store.select(selectAuthState)]).pipe(
    filter(([user, authState]) => !!user && !!authState),
    take(1),
    switchMap(([user]) => {
      if (!user) {
        console.warn('DataspaceRedirectGuard: User not found, redirecting to login.');
        return of(router.createUrlTree([AppRoutes.login]));
      }

      // If the user was sent to Keycloak from a specific URL, restore it here
      // instead of falling back to the default dataspace.
      const savedUrl = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (savedUrl) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        return of(router.parseUrl(savedUrl));
      }

      if (user.defaultDataSpace.id) {
        return dataspaceRoutingService
          .navigateToDataspace(user.defaultDataSpace.id)
          .then(() => false);
      } else {
        return dataspaceRoutingService
          .navigateToFirstAvailableDataspace()
          .pipe(map(() => false));
      }
    }),
    catchError((error) => {
      console.error('DataspaceRedirectGuard: Failed to redirect:', error);
      return of(router.createUrlTree(['/no-dataspace']));
    })
  );
};
